<template>
  <div id="app">
    <!-- Record Details Popup -->
    <div v-if="selectedRecord" class="simple-popup" @click.self="selectedRecord = null">
      <div class="popup-content wide-popup">
        <div class="popup-header">
          <h3>Transaction Details</h3>
          <button @click="selectedRecord = null" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="detail-sections">
            <!-- Transaction Info -->
            <div class="detail-section">
              <h4>Transaction Information</h4>
              <div class="detail-row">
                <strong>Transaction ID:</strong> {{ selectedRecord.transactionID }}
              </div>
              <div class="detail-row">
                <strong>Record Status:</strong>
                <span :class="['status-badge', getRecordStatusClass(selectedRecord.recordStatus)]">
                  {{ selectedRecord.recordStatus }}
                </span>
              </div>
              <div class="detail-row">
                <strong>Account:</strong> {{ selectedRecord.account }}
              </div>
            </div>

            <!-- Android Data -->
            <div class="detail-section android-section">
              <h4><span class="source-icon">📱</span> Android Data</h4>
              <div class="detail-grid">
                <div class="detail-row">
                  <strong>ID:</strong> {{ selectedRecord.android_ID || 'N/A' }}
                </div>
                <div class="detail-row">
                  <strong>Volume:</strong> {{ formatVolume(selectedRecord.android_Volume) }}
                </div>
                <div class="detail-row">
                  <strong>Date:</strong> {{ formatDateTime(selectedRecord.android_Createdate) }}
                </div>
                <div class="detail-row">
                  <strong>Store:</strong> {{ selectedRecord.android_Store || 'N/A' }}
                </div>
                <div class="detail-row">
                  <strong>Equipment Tag:</strong> {{ selectedRecord.android_EquipmentTagfromField || 'N/A' }}
                </div>
              </div>
            </div>

            <!-- Log Data -->
            <div class="detail-section log-section">
              <h4><span class="source-icon">📋</span> Log Data</h4>
              <div class="detail-grid">
                <div class="detail-row">
                  <strong>ID:</strong> {{ selectedRecord.log_ID || 'N/A' }}
                </div>
                <div class="detail-row">
                  <strong>Volume:</strong> {{ formatVolume(selectedRecord.log_Volume) }}
                </div>
                <div class="detail-row">
                  <strong>Date:</strong> {{ formatDateTime(selectedRecord.log_Createdate) }}
                </div>
                <div class="detail-row">
                  <strong>Store:</strong> {{ selectedRecord.log_Store || 'N/A' }}
                </div>
                <div class="detail-row">
                  <strong>Totalizer Volume:</strong> {{ selectedRecord.log_TotalizerVolume || 0 }}
                </div>
              </div>
            </div>

            <!-- IOT Data -->
            <div class="detail-section iot-section">
              <h4><span class="source-icon">🌐</span> IOT Data</h4>
              <div class="detail-grid">
                <div class="detail-row">
                  <strong>ID:</strong> {{ selectedRecord.ioT_ID || 'N/A' }}
                </div>
                <div class="detail-row">
                  <strong>Device ID:</strong> {{ selectedRecord.ioT_DeviceID || 'N/A' }}
                </div>
                <div class="detail-row">
                  <strong>Volume:</strong> {{ formatVolume(selectedRecord.ioT_Volume) }}
                </div>
                <div class="detail-row">
                  <strong>Date:</strong> {{ formatDateTime(selectedRecord.ioT_Createdate) }}
                </div>
              </div>
            </div>

            <!-- Variance Analysis -->
            <div class="detail-section variance-section">
              <h4><span class="source-icon">📊</span> Variance Analysis</h4>
              <div class="variance-grid">
                <div class="variance-item">
                  <span class="variance-label">Android-Log Diff:</span>
                  <span :class="getVarianceClass(selectedRecord.android_Log_Variance_Level)">
                    {{ formatVolume(selectedRecord.android_Log_VolumeDiff) }}
                    ({{ selectedRecord.android_Log_Variance_Level }})
                  </span>
                </div>
                <div class="variance-item">
                  <span class="variance-label">Android-IOT Diff:</span>
                  <span :class="getVarianceClass(selectedRecord.android_IOT_Variance_Level)">
                    {{ formatVolume(selectedRecord.android_IOT_VolumeDiff) }}
                    ({{ selectedRecord.android_IOT_Variance_Level }})
                  </span>
                </div>
                <div class="variance-item">
                  <span class="variance-label">Log-IOT Diff:</span>
                  <span :class="getVarianceClass(selectedRecord.log_IOT_Variance_Level)">
                    {{ formatVolume(selectedRecord.log_IOT_VolumeDiff) }}
                    ({{ selectedRecord.log_IOT_Variance_Level }})
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics Breakdown Popup -->
    <div v-if="showStatsBreakdown" class="simple-popup" @click.self="showStatsBreakdown = false">
      <div class="popup-content stats-popup">
        <div class="popup-header">
          <h3>{{ statsBreakdownTitle }}</h3>
          <button @click="showStatsBreakdown = false" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="stats-summary">
            <div class="summary-item">
              <span class="summary-label">Total Records:</span>
              <span class="summary-value">{{ statsBreakdownData.total }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Time Period:</span>
              <span class="summary-value">{{ formatDateRange() }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Filter Applied:</span>
              <span class="summary-value">{{ statsBreakdownData.filterType }}</span>
            </div>
          </div>

          <div class="breakdown-container">
            <h4>Breakdown by Store</h4>
            <div class="store-breakdown">
              <div v-for="store in statsBreakdownData.byStore" :key="store.name" class="store-item">
                <div class="store-header" @click="store.expanded = !store.expanded">
                  <div class="store-info">
                    <strong>{{ store.name }}</strong>
                    <span class="count-badge">{{ store.count }} records</span>
                  </div>
                  <div class="store-progress">
                    <div class="progress-bar">
                      <div class="progress-fill" :style="{ width: store.percentage + '%', background: getProgressColor(statsBreakdownData.filterType) }"></div>
                    </div>
                    <span class="percentage-text">{{ store.percentage.toFixed(1) }}%</span>
                  </div>
                </div>

                <div v-if="store.expanded" class="store-details">
                  <div class="detail-grid-small">
                    <div class="detail-item">
                      <span class="detail-label">Total Volume:</span>
                      <span class="detail-value">{{ formatVolume(store.totalVolume) }} L</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Avg Volume:</span>
                      <span class="detail-value">{{ formatVolume(store.avgVolume) }} L</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">First Transaction:</span>
                      <span class="detail-value">{{ formatDateTime(store.firstTransaction) }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Last Transaction:</span>
                      <span class="detail-value">{{ formatDateTime(store.lastTransaction) }}</span>
                    </div>
                  </div>

                  <div v-if="statsBreakdownData.filterType === 'Significant Variances'" class="variance-breakdown">
                    <h5>Variance Types:</h5>
                    <div class="variance-type-grid">
                      <div class="variance-type-item">
                        <span class="variance-type-label">Android-Log:</span>
                        <span class="variance-type-count">{{ store.varianceTypes.androidLog || 0 }}</span>
                      </div>
                      <div class="variance-type-item">
                        <span class="variance-type-label">Android-IOT:</span>
                        <span class="variance-type-count">{{ store.varianceTypes.androidIot || 0 }}</span>
                      </div>
                      <div class="variance-type-item">
                        <span class="variance-type-label">Log-IOT:</span>
                        <span class="variance-type-count">{{ store.varianceTypes.logIot || 0 }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="statsBreakdownData.byStore.length === 0" class="no-data">
              No records found for the selected filter.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Header -->
    <div class="header-toolbar">
      <div class="header-content">
        <div class="logo-section">
          <div class="logo-icon">
            <span class="icon-pulse">⚡</span>
          </div>
          <h1 class="dashboard-title">FAMS Data source comparison</h1>
        </div>
        <div class="header-controls">
          <div class="loader-controls">
            <div class="loader-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="autoRefreshEnabled" @change="handleAutoRefreshToggle" class="toggle-checkbox">
                <span class="toggle-text">Auto Refresh</span>
              </label>
            </div>
            <div v-if="autoRefreshEnabled" class="refresh-interval">
              <label class="interval-label">
                <span class="interval-text">Every:</span>
                <select v-model="refreshInterval" @change="handleRefreshIntervalChange" class="interval-select">
                  <option :value="30000">30 sec</option>
                  <option :value="60000">1 min</option>
                  <option :value="300000">5 min</option>
                  <option :value="600000">10 min</option>
                </select>
              </label>
            </div>
            <!-- Add manual refresh button here -->
            <button class="header-refresh-btn" @click="refreshData" title="Refresh Now">
              <span class="refresh-icon">🔄</span>
              <span>Refresh</span>
            </button>
          </div>
          <div class="live-indicator">
            <div :class="['live-dot', { 'auto-refresh': autoRefreshEnabled }]"></div>
            <span style="color: white; font-size: 0.875rem;">
              {{ autoRefreshEnabled ? 'Live (' + formatRefreshInterval() + ')' : 'Live' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Statistics Section -->
      <div class="stats-section">
        <div class="stats-header">
          <h3 class="stats-title">Data Comparison Overview</h3>
          <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
            <span class="toggle-icon">{{ statsVisible ? '📊' : '📈' }}</span>
            {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
          </button>
        </div>

        <transition name="slide-fade">
          <div v-if="statsVisible" class="stats-wrapper">
            <div class="stats-container">
              <!-- Total Records -->
              <div class="stat-card clickable-stat total-stat" @click="clearAllFilters">
                <div class="stat-label">Total Transactions</div>
                <div class="stat-value">{{ summaryStats.totalRecords }}</div>
                <div class="stat-change positive">
                  <span class="stat-icon">📝</span>
                  <span>All sources</span>
                </div>
                <div class="stat-hint">Click to show all</div>
              </div>

              <!-- Android Only Records -->
              <div class="stat-card clickable-stat android-stat" @click="filterByRecordStatus('Only in Android')">
                <div class="stat-label">Android Only</div>
                <div class="stat-value" style="color: #4CAF50;">{{ summaryStats.androidOnly }}</div>
                <div class="stat-change">
                  <span class="stat-icon">📱</span>
                  <span>Missing in Log/IOT</span>
                </div>
                <div class="stat-hint">Click to filter</div>
              </div>

              <!-- Log Missing Records -->
              <div class="stat-card clickable-stat log-stat" @click="filterByRecordStatus('Missing in Log')">
                <div class="stat-label">Missing in Log</div>
                <div class="stat-value" style="color: #FF9800;">{{ summaryStats.missingInLog }}</div>
                <div class="stat-change warning">
                  <span class="stat-icon">📋</span>
                  <span>Not in log system</span>
                </div>
                <div class="stat-hint">Click to filter</div>
              </div>

              <!-- IOT Missing Records -->
              <div class="stat-card clickable-stat iot-stat" @click="filterByRecordStatus('Missing in IOT')">
                <div class="stat-label">Missing in IOT</div>
                <div class="stat-value" style="color: #2196F3;">{{ summaryStats.missingInIOT }}</div>
                <div class="stat-change warning">
                  <span class="stat-icon">🌐</span>
                  <span>Not in IOT system</span>
                </div>
                <div class="stat-hint">Click to filter</div>
              </div>

              <!-- Present in All -->
              <div class="stat-card clickable-stat complete-stat" @click="filterByRecordStatus('Present in All Systems')">
                <div class="stat-label">Complete Records</div>
                <div class="stat-value" style="color: #27AE60;">{{ summaryStats.presentInAll }}</div>
                <div class="stat-change positive">
                  <span class="stat-icon">✅</span>
                  <span>In all systems</span>
                </div>
                <div class="stat-hint">Click to filter</div>
              </div>

              <!-- Variance Alerts -->
              <div class="stat-card clickable-stat variance-stat" @click="filterByVariance">
                <div class="stat-label">Significant Variances</div>
                <div class="stat-value" style="color: #E74C3C;">{{ summaryStats.significantVariances }}</div>
                <div class="stat-change negative">
                  <span class="stat-icon">⚠️</span>
                  <span>Requires attention</span>
                </div>
                <div class="stat-hint">Click to filter</div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Filters Section -->
      <div class="search-section">
        <div class="filter-header">
          <h3 class="filter-title">Data Filters</h3>
          <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
            <span class="toggle-icon">{{ filtersVisible ? '📁' : '📂' }}</span>
            {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
          </button>
        </div>

        <transition name="slide-fade">
          <div v-if="filtersVisible" class="search-controls-wrapper">
            <div class="search-controls">
            <div class="search-group date-filter-group">
              <label class="search-label">Date Range <span class="date-range-showing">{{ activeDateRangeLabel }}</span></label>
              <div class="date-quick-buttons">
                <button v-for="opt in dateRangeOptions" :key="opt.value"
                  :class="['date-quick-btn', { active: selectedDateRange === opt.value }]"
                  @click="selectDateRange(opt.value)">{{ opt.label }}</button>
              </div>
            </div>
              <!-- Record Status Filter -->
              <div class="search-group">
                <label class="search-label">Record Status <span class="multi-badge" v-if="filters.recordStatuses.length">({{ filters.recordStatuses.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('recordStatus')">
                    <span v-if="filters.recordStatuses.length === 0" class="placeholder">All Status</span>
                    <span v-else class="selected-items">
                      {{ filters.recordStatuses.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.recordStatus" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllRecordStatuses" :checked="filters.recordStatuses.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="status in recordStatusOptions" :key="status.value">
                        <input type="checkbox" :value="status.value" v-model="filters.recordStatuses" @change="applyFilters">
                        <span :class="'status-' + status.class">{{ status.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Activity Type Filter -->
              <div class="search-group">
                <label class="search-label">Activity Type <span class="multi-badge" v-if="filters.activityTypes.length">({{ filters.activityTypes.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('activityType')">
                    <span v-if="filters.activityTypes.length === 0" class="placeholder">All Types</span>
                    <span v-else class="selected-items">
                      {{ filters.activityTypes.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.activityType" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllActivityTypes" :checked="filters.activityTypes.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="type in activityTypeOptions" :key="type.value">
                        <input type="checkbox" :value="type.value" v-model="filters.activityTypes" @change="applyFilters">
                        <span :class="'activity-' + type.class">{{ type.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Status Filter -->
              <div class="search-group">
                <label class="search-label">Status <span class="multi-badge" v-if="filters.statuses.length">({{ filters.statuses.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('status')">
                    <span v-if="filters.statuses.length === 0" class="placeholder">All Statuses</span>
                    <span v-else class="selected-items">
                      {{ filters.statuses.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.status" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllStatuses" :checked="filters.statuses.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="status in statusOptions" :key="status.value">
                        <input type="checkbox" :value="status.value" v-model="filters.statuses" @change="applyFilters">
                        <span :class="status.class">{{ status.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Variance Level Filter -->
              <div class="search-group">
                <label class="search-label">Variance Level</label>
                <select v-model="filters.varianceLevel" @change="applyFilters" class="search-input">
                  <option value="">All Levels</option>
                  <option value="OK">OK</option>
                  <option value="Significant">Significant</option>
                </select>
              </div>

              <!-- Store Filter -->
              <div class="search-group">
                <label class="search-label">Store</label>
                <select v-model="filters.storeId" @change="applyFilters" class="search-input">
                  <option value="">All Stores</option>
                  <option v-for="store in uniqueStores" :key="store" :value="store">{{ store }}</option>
                </select>
              </div>

              <!-- Visible Columns Filter -->
              <div class="search-group">
                <label class="search-label">
                  Visible Columns
                  <span class="multi-badge orange-badge">
                    ({{ selectedColumns.length }}/{{ availableColumns.length }})
                  </span>
                </label>
                <div class="multi-select-container">
                  <div class="multi-select-display columns-select" @click="toggleDropdown('columns')">
                    <span v-if="selectedColumns.length === availableColumns.length" class="selected-items">
                      All columns selected
                    </span>
                    <span v-else class="selected-items">
                      {{ selectedColumns.length }} columns selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.columns" class="multi-select-dropdown columns-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllColumns" :checked="selectedColumns.length === availableColumns.length">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="column in availableColumns" :key="column.key">
                        <input type="checkbox" :value="column.key" v-model="selectedColumns" @change="handleColumnToggle">
                        <span>{{ column.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="filter-actions">
              <button class="apply-filters-btn" @click="applyFilters">
                Apply Filters
              </button>
              <button class="reset-filters-btn" @click="resetFilters">
                Reset
              </button>
            </div>
          </div>
        </transition>
      </div>

      <!-- Data Grid -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title">
            Android-IOT-Log Comparison Data
          </div>
          <div class="grid-controls">
            <div class="rows-selector">
              <span class="rows-label">Show:</span>
              <select v-model="itemsPerPage" @change="handleRowsChange" class="rows-select">
                <option :value="10">10 rows</option>
                <option :value="15">15 rows</option>
                <option :value="25">25 rows</option>
                <option :value="50">50 rows</option>
                <option :value="100">100 rows</option>
                <option :value="200">200 rows</option>
              </select>
            </div>
            <div class="grid-actions">
              <button class="action-button popout-btn" @click="popOutTable" title="Pop Out">
                <span class="action-icon">🔲</span>
                <span>Pop Out</span>
              </button>
              <button class="action-button refresh-btn" @click="refreshData" title="Refresh">
                <span class="action-icon">🔄</span>
                <span>Refresh</span>
              </button>
              <button class="action-button export-btn" @click="exportData" title="Export to CSV">
                <span class="action-icon">📥</span>
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        <div class="table-container">
          <div v-if="loading" class="loading-overlay">
            <div class="loader"></div>
          </div>

          <table v-if="!loading && filteredRecords.length > 0" class="data-table">
            <thead>
              <tr>
                <th v-for="column in visibleColumns" :key="column.key">
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="record in paginatedRecords" :key="record.transactionID"
                  @click="showRecordDetails(record)"
                  :class="getRowClass(record)">

                <!-- Dynamic columns based on selection -->
                <td v-for="column in visibleColumns" :key="column.key">
                  <!-- Date Column -->
                  <div v-if="column.key === 'date'" class="date-cell">
                    {{ formatDateTime(record.android_Createdate || record.log_Createdate || record.ioT_Createdate) }}
                  </div>

                  <!-- Device Column -->
                  <div v-if="column.key === 'device'" class="device-cell">
                    <code class="device-code">{{ record.ioT_DeviceID || record.android_EquipmentTagfromField || 'N/A' }}</code>
                  </div>

                  <!-- Store Name Column -->
                  <span v-if="column.key === 'storeName'" class="store-name">
                    {{ record.android_Store || record.log_Store || 'N/A' }}
                  </span>

                  <!-- Activity Type Column -->
                  <span v-if="column.key === 'activityType'" class="activity-badge">
                    {{ getActivityType(record) }}
                  </span>

                  <!-- Transaction ID Column -->
                  <code v-if="column.key === 'transactionId'" class="transaction-id">
                    {{ record.transactionID }}
                  </code>

                  <!-- Volume Column -->
                  <div v-if="column.key === 'volume'" class="volume-primary">
                    {{ formatVolume(record.android_Volume || record.log_Volume || record.ioT_Volume) }}
                  </div>

                  <!-- Details Column -->
                  <div v-if="column.key === 'details'" class="details-cell">
                    <span v-if="record.recordStatus === 'Present in All Systems'">
                      All systems synchronized
                    </span>
                    <span v-else-if="record.recordStatus === 'Only in Android'">
                      Missing in Log & IOT
                    </span>
                    <span v-else-if="record.recordStatus === 'Missing in Log'">
                      Log system not updated
                    </span>
                    <span v-else-if="record.recordStatus === 'Missing in IOT'">
                      IOT device not reporting
                    </span>
                  </div>

                  <!-- Status Column -->
                  <div v-if="column.key === 'status'" class="status-cell">
                    <span :class="['status-indicator', getStatusClass(record)]">
                      <span class="status-dot"></span>
                      {{ getRecordHealthStatus(record) }}
                    </span>
                  </div>

                  <!-- Record Status Column -->
                  <span v-if="column.key === 'recordStatus'" :class="['status-badge', getRecordStatusClass(record.recordStatus)]">
                    {{ record.recordStatus }}
                  </span>

                  <!-- Account Column -->
                  <span v-if="column.key === 'account'" class="account-name">
                    {{ record.account }}
                  </span>

                  <!-- Android Volume Column -->
                  <div v-if="column.key === 'androidVolume'" class="volume-cell android-volume">
                    <span class="source-indicator android">A</span>
                    {{ formatVolume(record.android_Volume) }} L
                  </div>

                  <!-- Log Volume Column -->
                  <div v-if="column.key === 'logVolume'" class="volume-cell log-volume">
                    <span class="source-indicator log">L</span>
                    {{ formatVolume(record.log_Volume) }} L
                  </div>

                  <!-- IOT Volume Column -->
                  <div v-if="column.key === 'iotVolume'" class="volume-cell iot-volume">
                    <span class="source-indicator iot">I</span>
                    {{ formatVolume(record.ioT_Volume) }} L
                  </div>

                  <!-- Android Store Column -->
                  <span v-if="column.key === 'androidStore'" class="store-name">
                    {{ record.android_Store || 'N/A' }}
                  </span>

                  <!-- Log Store Column -->
                  <span v-if="column.key === 'logStore'" class="store-name">
                    {{ record.log_Store || 'N/A' }}
                  </span>

                  <!-- Device ID Column -->
                  <code v-if="column.key === 'deviceId'" class="device-code">
                    {{ record.ioT_DeviceID || 'N/A' }}
                  </code>

                  <!-- Equipment Tag Column -->
                  <code v-if="column.key === 'equipmentTag'" class="equipment-tag">
                    {{ record.android_EquipmentTagfromField || record.log_EquipmentTagfromField || 'N/A' }}
                  </code>

                  <!-- Variance Column -->
                  <div v-if="column.key === 'variance'" class="variance-indicators">
                    <span :class="['variance-badge', getVarianceClass(record.android_Log_Variance_Level)]" title="Android-Log">
                      A-L: {{ record.android_Log_Variance_Level }}
                    </span>
                    <span :class="['variance-badge', getVarianceClass(record.android_IOT_Variance_Level)]" title="Android-IOT">
                      A-I: {{ record.android_IOT_Variance_Level }}
                    </span>
                    <span :class="['variance-badge', getVarianceClass(record.log_IOT_Variance_Level)]" title="Log-IOT">
                      L-I: {{ record.log_IOT_Variance_Level }}
                    </span>
                  </div>

                  <!-- Type Code Column -->
                  <span v-if="column.key === 'typeCode'" class="type-code">
                    {{ record.ioT_TypeID || record.android_ProductID || 'N/A' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!loading && filteredRecords.length === 0" class="empty-state">
            <div class="empty-icon">📂</div>
            <div class="empty-title">No Records Found</div>
            <div class="empty-description">
              Try adjusting your filters or check back later for new data
            </div>
          </div>
        </div>

        <div v-if="totalPages > 1" class="pagination-container">
          <div class="pagination-info">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
            {{ Math.min(currentPage * itemsPerPage, filteredRecords.length) }}
            of {{ filteredRecords.length }} entries
          </div>
          <div class="pagination-controls">
            <button
              class="page-btn prev-btn"
              @click="currentPage = 1"
              :disabled="currentPage === 1">
              First
            </button>
            <button
              class="page-btn prev-btn"
              @click="currentPage--"
              :disabled="currentPage === 1">
              Previous
            </button>
            <button
              v-for="page in visiblePageNumbers"
              :key="page"
              class="page-btn page-number"
              :class="{ 'active': page === currentPage }"
              @click="currentPage = page">
              {{ page }}
            </button>
            <button
              class="page-btn next-btn"
              @click="currentPage++"
              :disabled="currentPage === totalPages">
              Next
            </button>
            <button
              class="page-btn next-btn"
              @click="currentPage = totalPages"
              :disabled="currentPage === totalPages">
              Last
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FamsAndroidIOTComparisonView',

  data: function() {
    return {
      // Popup states
      selectedRecord: null,
      showStatsBreakdown: false,
      statsBreakdownTitle: '',
      statsBreakdownData: {
        total: 0,
        filterType: '',
        byStore: []
      },

      // Auto-refresh settings
      autoRefreshEnabled: false,
      refreshInterval: 60000,
      refreshTimer: null,

      // Loading states
      loading: false,

      // API Configuration
      apiConfig: {
        baseURL: process.env.VUE_APP_API_URL || '',
        accountId: localStorage.getItem('userAccountId')
      },

      // Pagination
      currentPage: 1,
      itemsPerPage: 25,

      // Main data
      records: [],

      // Summary statistics
      summaryStats: {
        totalRecords: 0,
        androidOnly: 0,
        missingInLog: 0,
        missingInIOT: 0,
        presentInAll: 0,
        significantVariances: 0
      },

      // Filters
      filters: {
        startDateTime: this.getDefaultStartDate(),
        endDateTime: this.getDefaultEndDate(),
        recordStatuses: [],
        activityTypes: [],
        statuses: [],
        varianceLevel: '',
        storeId: '',
        specificDate: ''
      },
      selectedDateRange: 'last3days',
      dateRangeOptions: [
        { value: 'last3days', label: 'Last 3 Days' },
        { value: 'today',     label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: '2daysago',  label: '2 Days Ago' }
      ],
      filtersVisible: true,
      statsVisible: true,

      // Dropdown states
      dropdownStates: {
        recordStatus: false,
        activityType: false,
        status: false,
        columns: false
      },

      // Filter options
      recordStatusOptions: [
        { value: 'Present in All Systems', label: 'Present in All Systems', class: 'all-systems' },
        { value: 'Only in Android', label: 'Only in Android', class: 'android-only' },
        { value: 'Missing in Log', label: 'Missing in Log', class: 'missing-log' },
        { value: 'Missing in IOT', label: 'Missing in IOT', class: 'missing-iot' }
      ],

      activityTypeOptions: [
        { value: 'SYNC', label: 'Synchronized', class: 'activity-sync' },
        { value: 'ANDROID', label: 'Android Only', class: 'activity-android' },
        { value: 'NO_LOG', label: 'No Log', class: 'activity-no-log' },
        { value: 'NO_IOT', label: 'No IOT', class: 'activity-no-iot' }
      ],

      statusOptions: [
        { value: 'Processed', label: 'Processed', class: 'status-processed' },
        { value: 'Error', label: 'Error', class: 'status-error' },
        { value: 'Warning', label: 'Warning', class: 'status-warning' }
      ],

      // Unique values for filters
      uniqueStores: [],

      // Column visibility management
      availableColumns: [
        { key: 'date', label: 'Date', visible: true, order: 1 },
        { key: 'transactionId', label: 'Transaction ID', visible: true, order: 2 },
        { key: 'status', label: 'Status', visible: true, order: 3 },
        { key: 'recordStatus', label: 'Record Status', visible: true, order: 4 },
        { key: 'device', label: 'Device', visible: true, order: 5 },
        { key: 'storeName', label: 'Store Name', visible: true, order: 6 },
        { key: 'activityType', label: 'Activity Type', visible: true, order: 7 },
        { key: 'androidVolume', label: 'Android Volume (L)', visible: true, order: 8 },
        { key: 'logVolume', label: 'Log Volume (L)', visible: true, order: 9 },
        { key: 'iotVolume', label: 'IOT Volume (L)', visible: true, order: 10 },
        { key: 'details', label: 'Details', visible: true, order: 11 },
        { key: 'volume', label: 'Volume (L)', visible: false, order: 12 },
        { key: 'account', label: 'Account', visible: false, order: 13 },
        { key: 'androidStore', label: 'Android Store', visible: false, order: 14 },
        { key: 'logStore', label: 'Log Store', visible: false, order: 15 },
        { key: 'deviceId', label: 'Device ID', visible: false, order: 16 },
        { key: 'equipmentTag', label: 'Equipment Tag', visible: false, order: 17 },
        { key: 'variance', label: 'Variance Analysis', visible: false, order: 18 },
        { key: 'typeCode', label: 'Type Code', visible: false, order: 19 }
      ],
      selectedColumns: ['date', 'transactionId', 'status', 'recordStatus', 'device', 'storeName', 'activityType', 'androidVolume', 'logVolume', 'iotVolume', 'details']
    };
  },

  computed: {
    visibleColumns: function() {
      var self = this;
      // Sort columns by order and filter by selected
      return this.availableColumns
        .filter(function(col) {
          return self.selectedColumns.indexOf(col.key) !== -1;
        })
        .sort(function(a, b) {
          return a.order - b.order;
        });
    },

    minAllowedDate: function() {
      var d = new Date();
      d.setDate(d.getDate() - 3);
      return d.toISOString().split('T')[0];
    },

    maxAllowedDate: function() {
      return new Date().toISOString().split('T')[0];
    },

    activeDateRangeLabel: function() {
      if (this.filters.specificDate)
        return 'Showing: ' + this.filters.specificDate + ' (00:00 – 23:59)';
      var opt = this.dateRangeOptions.find(function(o) { return o.value === this.selectedDateRange; }.bind(this));
      return opt ? opt.label : '';
    },

    filteredRecords: function() {
      var filtered = this.records.slice();
      var self = this;

      // Date filters - check android_Createdate first, fallback to others
      if (this.filters.startDateTime) {
        var startDate = new Date(this.filters.startDateTime);
        filtered = filtered.filter(function(r) {
          var date = r.android_Createdate || r.log_Createdate || r.ioT_Createdate;
          return date && new Date(date) >= startDate;
        });
      }

      if (this.filters.endDateTime) {
        var endDate = new Date(this.filters.endDateTime);
        filtered = filtered.filter(function(r) {
          var date = r.android_Createdate || r.log_Createdate || r.ioT_Createdate;
          return date && new Date(date) <= endDate;
        });
      }

      // Record status filter
      if (this.filters.recordStatuses.length > 0) {
        var statuses = this.filters.recordStatuses;
        filtered = filtered.filter(function(r) {
          return statuses.indexOf(r.recordStatus) !== -1;
        });
      }

      // Activity type filter
      if (this.filters.activityTypes.length > 0) {
        var activityTypes = this.filters.activityTypes;
        filtered = filtered.filter(function(r) {
          return activityTypes.indexOf(self.getActivityType(r)) !== -1;
        });
      }

      // Status filter
      if (this.filters.statuses.length > 0) {
        var statusList = this.filters.statuses;
        filtered = filtered.filter(function(r) {
          return statusList.indexOf(self.getRecordHealthStatus(r)) !== -1;
        });
      }

      // Variance level filter
      if (this.filters.varianceLevel) {
        var level = this.filters.varianceLevel;
        filtered = filtered.filter(function(r) {
          return r.android_Log_Variance_Level === level ||
                 r.android_IOT_Variance_Level === level ||
                 r.log_IOT_Variance_Level === level;
        });
      }

      // Store filter
      if (this.filters.storeId) {
        var store = this.filters.storeId;
        filtered = filtered.filter(function(r) {
          return r.android_Store === store || r.log_Store === store;
        });
      }

      return filtered;
    },

    totalPages: function() {
      return Math.ceil(this.filteredRecords.length / this.itemsPerPage);
    },

    paginatedRecords: function() {
      var start = (this.currentPage - 1) * this.itemsPerPage;
      var end = start + this.itemsPerPage;
      return this.filteredRecords.slice(start, end);
    },

    visiblePageNumbers: function() {
      var maxVisible = 5;
      var pages = [];
      var start = Math.max(1, this.currentPage - 2);
      var end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (var i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },

  methods: {
    getDefaultStartDate: function() {
      var date = new Date();
      date.setDate(date.getDate() - 3);
      date.setHours(0, 0, 0, 0);
      return this.formatDateForInput(date);
    },

    getDefaultEndDate: function() {
      var date = new Date();
      date.setHours(23, 59, 59, 999);
      return this.formatDateForInput(date);
    },

    formatDateForInput: function(date) {
      var year = date.getFullYear();
      var month = String(date.getMonth() + 1).padStart(2, '0');
      var day = String(date.getDate()).padStart(2, '0');
      var hours = String(date.getHours()).padStart(2, '0');
      var minutes = String(date.getMinutes()).padStart(2, '0');

      return year + '-' + month + '-' + day + 'T' + hours + ':' + minutes;
    },

    selectDateRange: function(rangeValue) {
      this.selectedDateRange = rangeValue;
      this.filters.specificDate = '';
      var now = new Date();
      var offsets = { last3days: -3, today: 0, yesterday: -1, '2daysago': -2 };
      var offset = offsets[rangeValue];

      var start = new Date(now);
      start.setDate(start.getDate() + (rangeValue === 'last3days' ? -3 : offset));
      start.setHours(0, 0, 0, 0);

      var end = new Date(now);
      if (rangeValue !== 'last3days') end.setDate(end.getDate() + offset);
      end.setHours(23, 59, 59, 999);

      this.filters.startDateTime = this.formatDateForInput(start);
      this.filters.endDateTime   = this.formatDateForInput(end);
      this.applyFilters();
    },

    onSpecificDateChange: function() {
      if (!this.filters.specificDate) return;
      this.selectedDateRange = '';
      var parts = this.filters.specificDate.split('-');
      var y = parts[0];
      var m = parts[1];
      var d = parts[2];
      var start = new Date(y, m - 1, d, 0, 0, 0, 0);
      var end   = new Date(y, m - 1, d, 23, 59, 59, 999);
      this.filters.startDateTime = this.formatDateForInput(start);
      this.filters.endDateTime   = this.formatDateForInput(end);
      this.applyFilters();
    },

    loadComparisonData: function() {
      var self = this;

      self.loading = true;
      var start = self.formatDateTime(self.filters.startDateTime);
      var end = self.formatDateTime(self.filters.endDateTime);

      var onSuccess = function(response) {
        console.log('API Response received:', response);

        // Direct data assignment from the response
        if (response.data && Array.isArray(response.data)) {
          self.records = response.data;
          self.calculateSummaryStats();
          self.extractUniqueValues();

          self.showNotification('Success', 'Loaded ' + self.records.length + ' comparison records', 'success');
        } else {
          console.error('Invalid data format in response');
          self.records = [];
        }
      };

      var onError = function(error) {
        console.error('API Error:', error);
        self.loading = false;
        self.showNotification('Error', 'Failed to load data: ' + (error.message || 'Unknown error'), 'danger');
      };

      var onFinally = function() {
        console.log('Data load attempt finished.');
        self.loading = false;
      };

      // API call with updated endpoint name
      self.$ajaxGet(
        self,
        'VwIotdataNotificationComprehensiveActivity/GetIotProcessInformationAndroidIOTLog?accountId=' + self.apiConfig.accountId + `&startDate=${start}&endDate=${end}`,
        onSuccess,
        onFinally,
        onError
      );
    },

    calculateSummaryStats: function() {
      var stats = {
        totalRecords: this.records.length,
        androidOnly: 0,
        missingInLog: 0,
        missingInIOT: 0,
        presentInAll: 0,
        significantVariances: 0
      };

      this.records.forEach(function(record) {
        // Count by record status
        switch(record.recordStatus) {
          case 'Only in Android':
            stats.androidOnly++;
            break;
          case 'Missing in Log':
            stats.missingInLog++;
            break;
          case 'Missing in IOT':
            stats.missingInIOT++;
            break;
          case 'Present in All Systems':
            stats.presentInAll++;
            break;
        }

        // Count significant variances
        if (record.android_Log_Variance_Level === 'Significant' ||
            record.android_IOT_Variance_Level === 'Significant' ||
            record.log_IOT_Variance_Level === 'Significant') {
          stats.significantVariances++;
        }
      });

      this.summaryStats = stats;
    },

    extractUniqueValues: function() {
      var stores = {};

      this.records.forEach(function(record) {
        // Collect unique stores
        if (record.android_Store) stores[record.android_Store] = true;
        if (record.log_Store && record.log_Store !== 'N/A') stores[record.log_Store] = true;
      });

      // Convert to arrays
      this.uniqueStores = Object.keys(stores).sort();
    },

    getRecordStatusClass: function(status) {
      switch(status) {
        case 'Present in All Systems':
          return 'status-all-systems';
        case 'Only in Android':
          return 'status-android-only';
        case 'Missing in Log':
          return 'status-missing-log';
        case 'Missing in IOT':
          return 'status-missing-iot';
        default:
          return '';
      }
    },

    getVarianceClass: function(level) {
      return level === 'Significant' ? 'variance-significant' : 'variance-ok';
    },

    getRowClass: function(record) {
      var classes = [];

      // Add status-based class
      if (record.recordStatus === 'Only in Android') {
        classes.push('row-android-only');
      } else if (record.recordStatus === 'Missing in Log') {
        classes.push('row-missing-log');
      } else if (record.recordStatus === 'Missing in IOT') {
        classes.push('row-missing-iot');
      } else if (record.recordStatus === 'Present in All Systems') {
        classes.push('row-all-systems');
      }

      // Add variance alert class
      if (record.android_Log_Variance_Level === 'Significant' ||
          record.android_IOT_Variance_Level === 'Significant' ||
          record.log_IOT_Variance_Level === 'Significant') {
        classes.push('row-variance-alert');
      }

      return classes.join(' ');
    },

    formatVolume: function(volume) {
      if (volume === null || volume === undefined) return 'N/A';
      return parseFloat(volume).toFixed(2);
    },

    formatDateTime: function(date) {
      if (!date) return 'N/A';
      var d = new Date(date);

      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      var hours = String(d.getHours()).padStart(2, '0');
      var minutes = String(d.getMinutes()).padStart(2, '0');

      return year + '/' + month + '/' + day + ' ' + hours + ':' + minutes;
    },

    showRecordDetails: function(record) {
      this.selectedRecord = record;
    },

    filterByRecordStatus: function(status) {
      this.filters.recordStatuses = [status];
      this.applyFilters();
      this.showStatisticsBreakdown('Record Status: ' + status, status);
    },

    clearAllFilters: function() {
      this.resetFilters();
      this.showStatisticsBreakdown('All Transactions', 'all');
    },

    filterByVariance: function() {
      this.filters.varianceLevel = 'Significant';
      this.applyFilters();
      this.showStatisticsBreakdown('Significant Variances', 'variance');
    },

    showStatisticsBreakdown: function(title, filterType) {
      var self = this;
      this.statsBreakdownTitle = title;

      // Get filtered data
      var dataToAnalyze = [];
      if (filterType === 'all') {
        dataToAnalyze = this.records;
      } else if (filterType === 'variance') {
        dataToAnalyze = this.records.filter(function(r) {
          return r.android_Log_Variance_Level === 'Significant' ||
                 r.android_IOT_Variance_Level === 'Significant' ||
                 r.log_IOT_Variance_Level === 'Significant';
        });
      } else {
        dataToAnalyze = this.records.filter(function(r) {
          return r.recordStatus === filterType;
        });
      }

      // Group by store
      var storeGroups = {};
      dataToAnalyze.forEach(function(record) {
        var storeName = record.android_Store || record.log_Store || 'Unknown Store';
        if (!storeGroups[storeName]) {
          storeGroups[storeName] = {
            name: storeName,
            count: 0,
            records: [],
            totalVolume: 0,
            firstTransaction: null,
            lastTransaction: null,
            varianceTypes: {
              androidLog: 0,
              androidIot: 0,
              logIot: 0
            }
          };
        }

        storeGroups[storeName].count++;
        storeGroups[storeName].records.push(record);

        // Add volumes
        var volume = parseFloat(record.android_Volume || record.log_Volume || record.ioT_Volume || 0);
        storeGroups[storeName].totalVolume += volume;

        // Track dates
        var date = record.android_Createdate || record.log_Createdate || record.ioT_Createdate;
        if (date) {
          if (!storeGroups[storeName].firstTransaction || date < storeGroups[storeName].firstTransaction) {
            storeGroups[storeName].firstTransaction = date;
          }
          if (!storeGroups[storeName].lastTransaction || date > storeGroups[storeName].lastTransaction) {
            storeGroups[storeName].lastTransaction = date;
          }
        }

        // Track variance types
        if (record.android_Log_Variance_Level === 'Significant') {
          storeGroups[storeName].varianceTypes.androidLog++;
        }
        if (record.android_IOT_Variance_Level === 'Significant') {
          storeGroups[storeName].varianceTypes.androidIot++;
        }
        if (record.log_IOT_Variance_Level === 'Significant') {
          storeGroups[storeName].varianceTypes.logIot++;
        }
      });

      // Convert to array and calculate percentages
      var totalRecords = dataToAnalyze.length;
      var storeData = Object.keys(storeGroups).map(function(storeName) {
        var store = storeGroups[storeName];
        return {
          name: store.name,
          count: store.count,
          percentage: totalRecords > 0 ? (store.count / totalRecords * 100) : 0,
          totalVolume: store.totalVolume,
          avgVolume: store.count > 0 ? store.totalVolume / store.count : 0,
          firstTransaction: store.firstTransaction,
          lastTransaction: store.lastTransaction,
          varianceTypes: store.varianceTypes,
          expanded: false
        };
      }).sort(function(a, b) {
        return b.count - a.count; // Sort by count descending
      });

      this.statsBreakdownData = {
        total: totalRecords,
        filterType: filterType === 'variance' ? 'Significant Variances' :
                    filterType === 'all' ? 'All Records' : filterType,
        byStore: storeData
      };

      this.showStatsBreakdown = true;
    },

    formatDateRange: function() {
      var start = this.filters.startDateTime ? new Date(this.filters.startDateTime).toLocaleDateString() : 'Start';
      var end = this.filters.endDateTime ? new Date(this.filters.endDateTime).toLocaleDateString() : 'End';
      return start + ' - ' + end;
    },

    getProgressColor: function(filterType) {
      switch(filterType) {
        case 'Only in Android': return '#4CAF50';
        case 'Missing in Log': return '#FF9800';
        case 'Missing in IOT': return '#2196F3';
        case 'Present in All Systems': return '#27AE60';
        case 'Significant Variances': return '#E74C3C';
        default: return '#FF8C00';
      }
    },

    toggleDropdown: function(type) {
      var self = this;
      Object.keys(this.dropdownStates).forEach(function(key) {
        if (key !== type) {
          self.dropdownStates[key] = false;
        }
      });
      this.dropdownStates[type] = !this.dropdownStates[type];
    },

    toggleAllRecordStatuses: function() {
      if (this.filters.recordStatuses.length === 0) {
        this.filters.recordStatuses = this.recordStatusOptions.map(function(opt) {
          return opt.value;
        });
      } else {
        this.filters.recordStatuses = [];
      }
      this.applyFilters();
    },

    toggleAllActivityTypes: function() {
      if (this.filters.activityTypes.length === 0) {
        this.filters.activityTypes = this.activityTypeOptions.map(function(opt) {
          return opt.value;
        });
      } else {
        this.filters.activityTypes = [];
      }
      this.applyFilters();
    },

    toggleAllStatuses: function() {
      if (this.filters.statuses.length === 0) {
        this.filters.statuses = this.statusOptions.map(function(opt) {
          return opt.value;
        });
      } else {
        this.filters.statuses = [];
      }
      this.applyFilters();
    },

    toggleAllColumns: function() {
      if (this.selectedColumns.length === this.availableColumns.length) {
        // Keep the essential columns when deselecting all
        this.selectedColumns = ['date', 'transactionId', 'status', 'recordStatus', 'device', 'storeName', 'activityType', 'androidVolume', 'logVolume', 'iotVolume'];
      } else {
        this.selectedColumns = this.availableColumns.map(function(col) {
          return col.key;
        });
      }
    },

    handleColumnToggle: function() {
      // Ensure at least one column is always selected
      if (this.selectedColumns.length === 0) {
        this.selectedColumns = ['transactionId'];
      }
    },

    getActivityType: function(record) {
      if (record.recordStatus === 'Present in All Systems') {
        return 'SYNC';
      } else if (record.recordStatus === 'Only in Android') {
        return 'ANDROID';
      } else if (record.recordStatus === 'Missing in Log') {
        return 'NO_LOG';
      } else if (record.recordStatus === 'Missing in IOT') {
        return 'NO_IOT';
      }
      return 'UNKNOWN';
    },

    getRecordHealthStatus: function(record) {
      if (record.android_Log_Variance_Level === 'Significant' ||
          record.android_IOT_Variance_Level === 'Significant' ||
          record.log_IOT_Variance_Level === 'Significant') {
        return 'Error';
      }
      if (record.recordStatus === 'Present in All Systems') {
        return 'Processed';
      }
      return 'Warning';
    },

    getStatusClass: function(record) {
      var status = this.getRecordHealthStatus(record);
      if (status === 'Processed') return 'status-success';
      if (status === 'Error') return 'status-error';
      return 'status-warning';
    },

    applyFilters: function() {
      this.currentPage = 1;
      this.showNotification('Success', 'Filters applied', 'success');
    },

 resetFilters: function() {
  this.filters = {
    startDateTime: this.getDefaultStartDate(),
    endDateTime: this.getDefaultEndDate(),
    recordStatuses: [],
    activityTypes: [],
    statuses: [],
    varianceLevel: '',
    storeId: '',
    specificDate: ''
  };
  this.selectedDateRange = 'last3days';

  // Reset selected columns to default
  this.selectedColumns = [
    'date',
    'transactionId',
    'status',
    'recordStatus',
    'device',
    'storeName',
    'activityType',
    'androidVolume',
    'logVolume',
    'iotVolume',
    'details'
  ];

  this.currentPage = 1;
  this.showNotification('Success', 'Filters reset', 'success');
},

    refreshData: function() {
      this.showNotification('Info', 'Refreshing data...', 'primary');
      this.loadComparisonData();
    },

    exportData: function() {
      var self = this;

      var headers = [
        'Transaction ID', 'Record Status', 'Account',
        'Android Volume', 'Log Volume', 'IOT Volume',
        'Android Date', 'Log Date', 'IOT Date',
        'Android Store', 'Log Store',
        'Android-Log Diff', 'Android-IOT Diff', 'Log-IOT Diff',
        'Android-Log Variance', 'Android-IOT Variance', 'Log-IOT Variance'
      ];

      var csvRows = this.filteredRecords.map(function(record) {
        return [
          record.transactionID,
          record.recordStatus,
          record.account,
          self.formatVolume(record.android_Volume),
          self.formatVolume(record.log_Volume),
          self.formatVolume(record.ioT_Volume),
          self.formatDateTime(record.android_Createdate),
          self.formatDateTime(record.log_Createdate),
          self.formatDateTime(record.ioT_Createdate),
          record.android_Store || 'N/A',
          record.log_Store || 'N/A',
          self.formatVolume(record.android_Log_VolumeDiff),
          self.formatVolume(record.android_IOT_VolumeDiff),
          self.formatVolume(record.log_IOT_VolumeDiff),
          record.android_Log_Variance_Level,
          record.android_IOT_Variance_Level,
          record.log_IOT_Variance_Level
        ].map(function(val) {
          return '"' + (val || '') + '"';
        }).join(',');
      });

      var csvContent = [headers.join(',')].concat(csvRows).join('\n');

      var blob = new Blob([csvContent], { type: 'text/csv' });
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'fams_android_iot_comparison_' + new Date().getTime() + '.csv';
      a.click();
      window.URL.revokeObjectURL(url);

      this.showNotification('Success', 'Data exported successfully', 'success');
    },

    popOutTable: function() {
      // Create a new window with the current table data
      var self = this;
      var popupWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');

      if (!popupWindow) {
        this.showNotification('Error', 'Please allow popups for this site', 'danger');
        return;
      }

      // Build HTML for the popup window
      var html = '<!DOCTYPE html><html><head>';
      html += '<title>FAMS Android-IOT-Log Comparison - ' + new Date().toLocaleDateString() + '</title>';
      html += '<style>';
      html += 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 20px; }';
      html += 'h1 { color: #FF8C00; font-size: 1.5rem; margin-bottom: 20px; }';
      html += 'table { width: 100%; border-collapse: collapse; }';
      html += 'th { background: #FF8C00; color: white; padding: 12px; text-align: left; position: sticky; top: 0; }';
      html += 'td { padding: 10px; border-bottom: 1px solid #e0e0e0; }';
      html += 'tr:hover { background: #f5f5f5; }';
      html += '.status-all-systems { color: #27AE60; font-weight: bold; }';
      html += '.status-android-only { color: #4CAF50; font-weight: bold; }';
      html += '.status-missing-log { color: #FF9800; font-weight: bold; }';
      html += '.status-missing-iot { color: #2196F3; font-weight: bold; }';
      html += '.variance-ok { color: #4CAF50; }';
      html += '.variance-significant { color: #F44336; font-weight: bold; }';
      html += '.info { margin-bottom: 15px; color: #666; }';
      html += '</style>';
      html += '</head><body>';
      html += '<h1>FAMS Android-IOT-Log Comparison Report</h1>';
      html += '<div class="info">Generated: ' + new Date().toLocaleString() + '</div>';
      html += '<div class="info">Total Records: ' + this.filteredRecords.length + '</div>';
      html += '<table><thead><tr>';

      // Add headers based on visible columns
      this.visibleColumns.forEach(function(col) {
        html += '<th>' + col.label + '</th>';
      });
      html += '</tr></thead><tbody>';

      // Add data rows
      this.filteredRecords.forEach(function(record) {
        html += '<tr>';
        self.visibleColumns.forEach(function(col) {
          var value = '';
          var className = '';

          switch(col.key) {
            case 'date':
              value = self.formatDateTime(record.android_Createdate || record.log_Createdate || record.ioT_Createdate);
              break;
            case 'device':
              value = record.ioT_DeviceID || record.android_EquipmentTagfromField || 'N/A';
              break;
            case 'storeName':
              value = record.android_Store || record.log_Store || 'N/A';
              break;
            case 'activityType':
              value = self.getActivityType(record);
              break;
            case 'transactionId':
              value = record.transactionID;
              break;
            case 'volume':
              value = self.formatVolume(record.android_Volume || record.log_Volume || record.ioT_Volume);
              break;
            case 'details':
              if (record.recordStatus === 'Present in All Systems') {
                value = 'All systems synchronized';
              } else if (record.recordStatus === 'Only in Android') {
                value = 'Missing in Log & IOT';
              } else if (record.recordStatus === 'Missing in Log') {
                value = 'Log system not updated';
              } else {
                value = 'IOT device not reporting';
              }
              break;
            case 'status':
              value = self.getRecordHealthStatus(record);
              break;
            case 'recordStatus':
              value = record.recordStatus;
              className = self.getRecordStatusClass(record.recordStatus);
              break;
            case 'account':
              value = record.account;
              break;
            case 'androidVolume':
              value = self.formatVolume(record.android_Volume);
              break;
            case 'logVolume':
              value = self.formatVolume(record.log_Volume);
              break;
            case 'iotVolume':
              value = self.formatVolume(record.ioT_Volume);
              break;
            case 'variance':
              value = 'A-L:' + record.android_Log_Variance_Level +
                     ' A-I:' + record.android_IOT_Variance_Level +
                     ' L-I:' + record.log_IOT_Variance_Level;
              break;
            default:
              value = 'N/A';
          }

          html += '<td class="' + className + '">' + value + '</td>';
        });
        html += '</tr>';
      });

      html += '</tbody></table>';
      html += '</body></html>';

      popupWindow.document.write(html);
      popupWindow.document.close();

      this.showNotification('Success', 'Table opened in new window', 'success');
    },

    handleRowsChange: function() {
      this.currentPage = 1;
    },

    handleAutoRefreshToggle: function() {
      if (this.autoRefreshEnabled) {
        this.startAutoRefresh();
        this.showNotification('Success', 'Auto-refresh enabled', 'success');
      } else {
        this.stopAutoRefresh();
        this.showNotification('Info', 'Auto-refresh disabled', 'primary');
      }
    },

    handleRefreshIntervalChange: function() {
      if (this.autoRefreshEnabled) {
        this.stopAutoRefresh();
        this.startAutoRefresh();
      }
    },

    startAutoRefresh: function() {
      var self = this;
      this.stopAutoRefresh();

      if (this.autoRefreshEnabled) {
        this.refreshTimer = setInterval(function() {
          self.loadComparisonData();
        }, this.refreshInterval);
      }
    },

    stopAutoRefresh: function() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    formatRefreshInterval: function() {
      var seconds = this.refreshInterval / 1000;
      if (seconds < 60) {
        return seconds + 's';
      } else {
        return (seconds / 60) + 'm';
      }
    },

    showNotification: function(title, text, type) {
      if (this.$vs && this.$vs.notify) {
        this.$vs.notify({
          title: title,
          text: text,
          color: type,
          position: 'top-right'
        });
      } else {
        console.log(title + ': ' + text);
      }
    },

    handleClickOutside: function(event) {
      var self = this;
      var multiSelectContainers = this.$el.querySelectorAll('.multi-select-container');
      var clickedInside = false;

      multiSelectContainers.forEach(function(container) {
        if (container.contains(event.target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        Object.keys(this.dropdownStates).forEach(function(key) {
          self.dropdownStates[key] = false;
        });
      }
    }
  },

  mounted: function() {
    this.loadComparisonData();

    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }

    document.addEventListener('click', this.handleClickOutside);

    this.showNotification('Welcome!', 'FAMS Data source comparison - Ready', 'success');
  },

  beforeDestroy: function() {
    this.stopAutoRefresh();
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style>
:root {
  --android-color: #4CAF50;
  --log-color: #FF9800;
  --iot-color: #2196F3;
  --all-systems-color: #27AE60;
  --missing-color: #E74C3C;
  --variance-ok: #4CAF50;
  --variance-significant: #F44336;

  --autumn-primary: #D2691E;
  --autumn-accent: #FF8C00;
  --gradient-primary: linear-gradient(135deg, var(--autumn-primary) 0%, var(--autumn-accent) 100%);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #e2e8f0;
  height: 100vh;
  overflow: hidden;
  width: 100%;
}

#app {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: none;
}

/* Header */
.header-toolbar {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  padding: 16px 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 20px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: var(--autumn-primary);
}

.dashboard-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.loader-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
  accent-color: white;
}

.toggle-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.interval-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.interval-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.interval-select {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
}

/* Add these new styles for the dropdown options */
.interval-select option {
  background: #FF8C00;
  color: white;
}

.live-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
}

.interval-select:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.3);
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.live-dot.auto-refresh {
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

/* Main Content */
.main-content {
  flex: 1;
  overflow: visible;
  padding: 20px;
  width: 100%;
  background: #e2e8f0;
}

/* Statistics Section */
.stats-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  border: 1px solid #cbd5e1;
}

.stats-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.toggle-stats-btn, .toggle-filter-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.stats-wrapper {
  padding: 16px 20px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: visible;
}

.clickable-stat {
  cursor: pointer;
}

.clickable-stat:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  background: #ffffff !important;
}

.clickable-stat:hover .stat-hint {
  opacity: 1;
}

.stat-hint {
  position: absolute;
  bottom: 8px;
  right: 8px;
  font-size: 0.625rem;
  color: #94a3b8;
  opacity: 0;
  transition: opacity 0.3s ease;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-stat { border-left: 4px solid #FF8C00; }
.android-stat { border-left: 4px solid var(--android-color); }
.log-stat { border-left: 4px solid var(--log-color); }
.iot-stat { border-left: 4px solid var(--iot-color); }
.complete-stat { border-left: 4px solid #27AE60; }
.variance-stat { border-left: 4px solid #E74C3C; }

/* Statistics Breakdown Popup Styles */
.stats-popup {
  max-width: 800px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.stats-summary {
  display: flex;
  gap: 20px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 20px;
}

.summary-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #FF8C00;
}

.breakdown-container {
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
}

.breakdown-container h4 {
  margin: 0 0 16px 0;
  color: #334155;
  font-size: 1rem;
  padding-bottom: 8px;
  border-bottom: 2px solid #FF8C00;
}

.store-breakdown {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.store-item {
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.store-item:hover {
  border-color: #FF8C00;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.store-header {
  padding: 12px 16px;
  cursor: pointer;
  background: white;
  transition: background 0.2s ease;
}

.store-header:hover {
  background: #fff8f3;
}

.store-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.store-info strong {
  color: #334155;
  font-size: 0.95rem;
}

.count-badge {
  background: #FF8C00;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.store-progress {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #FF8C00;
  transition: width 0.3s ease;
}

.percentage-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  min-width: 45px;
  text-align: right;
}

.store-details {
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.detail-grid-small {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.detail-value {
  font-size: 0.875rem;
  color: #334155;
  font-weight: 600;
}

.variance-breakdown {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.variance-breakdown h5 {
  margin: 0 0 8px 0;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 600;
}

.variance-type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.variance-type-item {
  background: #fff8f3;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.variance-type-label {
  font-size: 0.625rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
}

.variance-type-count {
  font-size: 1.125rem;
  font-weight: 700;
  color: #E74C3C;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #94a3b8;
  font-style: italic;
}

.stat-label {
  color: #2c3e50;
  font-size: 0.875rem;
  margin-bottom: 8px;
  font-weight: 600;
}

.stat-value {
  font-size: 1.75rem;
  color: #2c3e50;
  font-weight: bold;
}

.stat-change {
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-change.positive { color: #27AE60; }
.stat-change.negative { color: #E74C3C; }
.stat-change.warning { color: #F39C12; }

/* Search/Filter Section */
.search-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  border: 1px solid #cbd5e1;
}

.filter-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.search-controls-wrapper {
  padding: 16px 20px;
}

.search-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.search-group {
  display: flex;
  flex-direction: column;
  position: relative;
}

.search-label {
  font-size: 0.875rem;
  color: #34495E;
  margin-bottom: 4px;
  font-weight: 500;
}

.search-input {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #ffffff;
}

.search-input:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.15);
}

/* Multi-select Dropdown */
.multi-select-container {
  position: relative;
}

.multi-select-display {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  min-height: 44px;
}

.multi-select-display:hover {
  border-color: #D2691E;
  background: #fff8f3;
}

.placeholder {
  color: #999;
}

.selected-items {
  color: #495057;
  font-weight: 500;
}

.dropdown-arrow {
  color: #999;
  font-size: 0.75rem;
  transition: transform 0.3s ease;
}

.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #D2691E;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.select-all-option {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #f8f9fa;
}

.dropdown-options {
  max-height: 200px;
  overflow-y: auto;
}

.checkbox-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.checkbox-option:hover {
  background: #fff3e0;
}

.checkbox-option input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #D2691E;
}

.multi-badge {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 4px;
}

.orange-badge {
  background: #FF8C00;
}

.columns-select {
  border: 2px solid #FF8C00;
  background: #FFF8F3;
}

.columns-dropdown {
  max-height: 400px;
}

/* New column styles */
.device-cell {
  font-family: monospace;
}

.device-code {
  background: #f0f9ff;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #0369a1;
}

.activity-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #475569;
  color: white;
}

.volume-primary {
  font-weight: 600;
  color: #FF8C00;
  font-size: 0.9rem;
}

.details-cell {
  color: #64748b;
  font-size: 0.875rem;
  font-style: italic;
}

.status-cell .status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-success {
  background: rgba(39, 174, 96, 0.1);
  color: #27AE60;
}

.status-error {
  background: rgba(231, 76, 60, 0.1);
  color: #E74C3C;
}

.status-warning {
  background: rgba(243, 156, 18, 0.1);
  color: #F39C12;
}

.equipment-tag {
  background: #f8fafc;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #475569;
}

.type-code {
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #334155;
}

/* Table header style update */
.data-table thead {
  position: sticky;
  top: 0;
  background: #FF8C00;
  z-index: 10;
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.apply-filters-btn, .reset-filters-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.apply-filters-btn {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
}

.apply-filters-btn:hover {
  background: linear-gradient(135deg, #B85918 0%, #E67C00 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(210, 105, 30, 0.3);
}

.reset-filters-btn {
  background: white;
  color: #34495E;
  border: 2px solid #FFE4B5;
}

.reset-filters-btn:hover {
  background: #FFF8F3;
  border-color: #FF8C00;
  transform: translateY(-1px);
}

/* Grid Container */
.grid-container {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid #cbd5e1;
}

.grid-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.grid-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
}

.grid-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.rows-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rows-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
}

.rows-select {
  padding: 6px 12px;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 100px;
}

.grid-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 8px 16px;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  white-space: nowrap;
}

.action-button:hover {
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-icon {
  font-size: 1rem;
}

/* Specific button colors */
.popout-btn {
  color: #8B5CF6;
  border-color: #DDD6FE;
}

.popout-btn:hover {
  background: #F3E8FF;
  border-color: #8B5CF6;
}

.refresh-btn {
  color: #3B82F6;
  border-color: #DBEAFE;
}

.refresh-btn:hover {
  background: #EFF6FF;
  border-color: #3B82F6;
}

.export-btn {
  color: #10B981;
  border-color: #D1FAE5;
}

.export-btn:hover {
  background: #ECFDF5;
  border-color: #10B981;
}

/* Remove old export button styles from filter section */
.filter-actions .export-btn {
  display: none;
}

/* Table */
.table-container {
  overflow: auto;
  position: relative;
  max-height: 600px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  z-index: 10;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.data-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  background: #ffffff;
  cursor: pointer;
}

.data-table tbody tr:hover {
  background: #f1f5f9;
}

.data-table tbody tr.row-android-only {
  background: rgba(76, 175, 80, 0.05);
}

.data-table tbody tr.row-missing-log {
  background: rgba(255, 152, 0, 0.05);
}

.data-table tbody tr.row-missing-iot {
  background: rgba(33, 150, 243, 0.05);
}

.data-table tbody tr.row-all-systems {
  background: rgba(39, 174, 96, 0.03);
}

.data-table tbody tr.row-variance-alert {
  border-left: 3px solid #F44336;
}

.data-table td {
  padding: 12px 16px;
  font-size: 0.875rem;
  color: #334155;
}

/* Transaction ID */
.transaction-id {
  font-family: monospace;
  font-size: 0.75rem;
  color: #64748b;
}

/* Status Badges */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-all-systems {
  background: rgba(39, 174, 96, 0.1);
  color: #27AE60;
  border: 1px solid rgba(39, 174, 96, 0.3);
}

.status-android-only {
  background: rgba(76, 175, 80, 0.1);
  color: #4CAF50;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.status-missing-log {
  background: rgba(255, 152, 0, 0.1);
  color: #FF9800;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.status-missing-iot {
  background: rgba(33, 150, 243, 0.1);
  color: #2196F3;
  border: 1px solid rgba(33, 150, 243, 0.3);
}

/* Volume Cells */
.volume-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.source-indicator {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.625rem;
}

.source-indicator.android {
  background: var(--android-color);
}

.source-indicator.log {
  background: var(--log-color);
}

.source-indicator.iot {
  background: var(--iot-color);
}

/* Variance Indicators */
.variance-indicators {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.variance-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
}

.variance-ok {
  background: rgba(76, 175, 80, 0.1);
  color: var(--variance-ok);
}

.variance-significant {
  background: rgba(244, 67, 54, 0.1);
  color: var(--variance-significant);
}

/* Account and Store Names */
.account-name {
  font-weight: 500;
  color: #475569;
}

.store-name {
  color: #64748b;
  font-size: 0.875rem;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #334155;
  background: #f8fafc;
  border-radius: 8px;
  margin: 20px;
}

.empty-icon {
  font-size: 4rem;
  color: #FFE4B5;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-description {
  color: #6c757d;
  text-align: center;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(241, 245, 249, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loader {
  width: 48px;
  height: 48px;
  border: 4px solid #FFE4B5;
  border-top: 4px solid #D2691E;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Pagination */
.pagination-container {
  padding: 16px 20px;
  border-top: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f1f5f9;
}

.pagination-info {
  color: #6c757d;
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  gap: 4px;
}

.page-btn {
  padding: 6px 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  color: #495057;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  min-width: 40px;
}

.page-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #D2691E;
  color: #D2691E;
}

.page-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Popup Styles */
.simple-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.popup-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  overflow: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.wide-popup {
  max-width: 900px;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-radius: 8px 8px 0 0;
}

.popup-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.popup-body {
  padding: 20px;
}

.detail-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-section {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.detail-section h4 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.source-icon {
  font-size: 1.25rem;
}

.android-section { border-left: 3px solid var(--android-color); }
.log-section { border-left: 3px solid var(--log-color); }
.iot-section { border-left: 3px solid var(--iot-color); }
.variance-section { border-left: 3px solid #9C27B0; }

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.detail-row {
  font-size: 0.9rem;
}

.detail-row strong {
  color: #34495E;
  display: inline-block;
  min-width: 100px;
}

.variance-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.variance-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background: white;
  border-radius: 6px;
}

.variance-label {
  font-weight: 600;
  color: #475569;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Status specific text colors in dropdowns */
.status-all-systems { color: #27AE60; }
.status-android-only { color: #4CAF50; }
.status-missing-log { color: #FF9800; }
.status-missing-iot { color: #2196F3; }

/* Activity type colors in dropdown */
.activity-activity-sync { color: #27AE60; font-weight: 600; }
.activity-activity-android { color: #4CAF50; font-weight: 600; }
.activity-activity-no-log { color: #FF9800; font-weight: 600; }
.activity-activity-no-iot { color: #2196F3; font-weight: 600; }

/* Status colors in dropdown */
.status-processed { color: #27AE60; font-weight: 600; }
.status-error { color: #E74C3C; font-weight: 600; }
.status-warning { color: #F39C12; font-weight: 600; }

/* Responsive Design */
@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }

  .stats-container {
    grid-template-columns: 1fr 1fr;
  }

  .search-controls {
    grid-template-columns: 1fr;
  }

  .data-table {
    font-size: 0.75rem;
  }

  .data-table th, .data-table td {
    padding: 8px;
  }

  .variance-indicators {
    flex-direction: row;
    flex-wrap: wrap;
  }
}

@media (max-width: 480px) {
  .header-content {
    flex-direction: column;
    gap: 15px;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .pagination-container {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: center;
  }
}

.header-refresh-btn {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
}

.header-refresh-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
}

.header-refresh-btn:active {
  transform: translateY(0);
}

.refresh-icon {
  font-size: 1rem;
  display: inline-block;
  transition: transform 0.3s ease;
}

.header-refresh-btn:hover .refresh-icon {
  transform: rotate(180deg);
}

/* Date Range Filter Styling */
.date-filter-group {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 8px 6px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.date-range-showing {
  font-size: 0.75rem;
  font-weight: 400;
  color: #64748b;
  text-transform: none;
  letter-spacing: 0;
  display: inline-block;
  margin-left: 6px;
  font-weight: normal;
}

.date-quick-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.date-quick-btn {
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  background: white;
  border-radius: 5px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.date-quick-btn:hover {
  border-color: #D2691E;
  color: #D2691E;
  background: #fff9f5;
}

.date-quick-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
  box-shadow: 0 2px 8px rgba(210, 105, 30, 0.2);
}
</style>
